import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormGroup, FormControl, Validators, ValidatorFn, ValidationErrors, AbstractControl } from '@angular/forms';
import { AuthFormNotificationType, AuthFormType } from '@shared/models/auth.interface';

import { AuthService } from '@shared/services/auth/auth.service';
import { LoadingService } from '@shared/services/core/loading.service';
import { Subscription } from 'rxjs';

const demoAnimation = trigger('demoAnimation', [
  transition(':enter', [
    style({ transform: 'translateX(40%)' }),
    animate('200ms', style({ transform: 'translateX(0%)' }))
  ]),
])

@Component({
  selector: 'app-password-recovery-form',
  templateUrl: './password-recovery-form.component.html',
  styleUrls: ['./password-recovery-form.component.scss'],
  animations: [demoAnimation]
})
export class PasswordRecoveryFormComponent implements OnInit, OnDestroy {
  @Input() accessCode: string = '';
  @Output() notify = new EventEmitter<AuthFormNotificationType | null>();
  @Output() formChange = new EventEmitter<AuthFormType>();

  passwordRecoverySubscription: Subscription | undefined;
  formValueChangesSubscription!: Subscription;
  fieldUsedForRecovery: "EMAILID" | "USERNAME" | undefined;
  formResetted: boolean = false;

  // using a separate flag for validation because the condition for form validity is a bit different (i.e. mutually exclusive fields).
  formIsValid: boolean = false;
  passwordRecoveryForm: FormGroup = new FormGroup({
    EmailId: new FormControl('', [Validators.email]),
    ClientId: new FormControl('', [clientIdFieldValidator()])
  })


  constructor(private authService: AuthService, private loadingService: LoadingService) { }

  ngOnInit(): void {
    this.formValueChangesSubscription = this.passwordRecoveryForm.valueChanges
    .subscribe(_ => {
      let eitherFieldsAreFilled = (!!this.passwordRecoveryForm.get('EmailId')?.value) || (!!this.passwordRecoveryForm.get('ClientId')?.value);
      let emailValid = !!this.passwordRecoveryForm.get('EmailId')?.valid 
      let emailProvided = !!(this.passwordRecoveryForm.get('EmailId')?.value);
      let usernameProvided = !!(this.passwordRecoveryForm.get('ClientId')?.value);
      let usernameValid = !!(this.passwordRecoveryForm.get('ClientId')?.valid);
      this.formIsValid = eitherFieldsAreFilled && ((emailProvided && emailValid) || (usernameProvided && usernameValid));

      if (this.formResetted) this.formResetted = false;
      else this.notify.next(null);
    });
  }

  recoverPassword() {
    if(!this.passwordRecoveryForm.get("EmailId")?.value && !this.passwordRecoveryForm.get("ClientId")?.value){  
      this.passwordRecoveryForm.get("EmailId")?.setErrors({ required: true });
      this.passwordRecoveryForm.get("ClientId")?.setErrors({ required: true });
      return;
    } else if (this.passwordRecoveryForm.get("EmailId")?.value) {
      this.fieldUsedForRecovery = "EMAILID";
    } else {
      this.fieldUsedForRecovery = "USERNAME";
    }

    this.notify.next(null);
    const form = this.passwordRecoveryForm.value;
    this.loadingService.showLoading();

    this.passwordRecoverySubscription = this.authService.recoverPassword({ ...form, AccessCode: this.accessCode }).subscribe(response => {
      if (response?.Data) {
        this.notify.next("PASS_RECOVERY_SUCCESS");
        this.formResetted = true;
        this.passwordRecoveryForm.reset();
      } else {
        if(this.fieldUsedForRecovery === "EMAILID") this.notify.next('PASS_RECOVERY_ERROR_WITH_EMAIL')
        else if(this.fieldUsedForRecovery === "USERNAME") this.notify.next('PASS_RECOVERY_ERROR_WITH_USERNAME')
        else this.notify.next("UNKNOWN_ERROR");
      }
      this.loadingService.stopLoading();
    });
  }

  disableAlternativeField(currentField: string, alternativeField: string) {
    this.passwordRecoveryForm.controls[currentField].enable();
    this.passwordRecoveryForm.controls[alternativeField].reset();
    this.passwordRecoveryForm.controls[alternativeField].disable();
  }

  enableAllFields() {
    this.passwordRecoveryForm.enable();

    const emailField = this.passwordRecoveryForm.controls['EmailId'];
    const clientIdField = this.passwordRecoveryForm.controls['ClientId'];

    if (emailField.touched && !emailField.value)
      this.passwordRecoveryForm.get("EmailId")?.setErrors({ required: true });
    if (clientIdField.touched && !clientIdField.value)
      this.passwordRecoveryForm.get("ClientId")?.setErrors({ required: true });

    this.passwordRecoveryForm.touched
  }

  ngOnDestroy(): void {
    this.formValueChangesSubscription.unsubscribe();
    this.passwordRecoverySubscription?.unsubscribe();
  }
}

function clientIdFieldValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const clientId = control.value;

    if (clientId && clientId.length > 0 && clientId.length < 10) return { invalidLength: true }
    else return null;
  }
}